import { useReducer, useEffect } from 'react';
import { useKeyboardNavigation } from './useKeyboardNavigation';
import { useListener } from './useListener';

const KEYS = {
    enter: 13,
    tab: 9,
};

const initialApiState = { items: [], loading: false };

export const AUTOCOMPLETE_DEBOUNCE_TIME = 250;

export const useAutocompleteApi = (
    search,
    extraParams,
    webApi,
    enabled,
    onChange = null,
    limit = 20
) => {
    const [state, dispatch] = useReducer((s, a) => {
        const type = typeof a === 'string' ? a : a.type;
        let nextState = s;
        let payload = null;

        switch (type) {
            case 'REQ':
                nextState = {
                    items: initialApiState.items,
                    loading: true,
                };
                break;
            case 'RES':
                payload = a.payload;
                nextState = {
                    items: payload && payload.slice ? payload.slice(0, limit) : [],
                    loading: false,
                };
                break;
            case 'ERROR':
            case 'RESET':
                nextState = initialApiState;
                break;
        }

        onChange && onChange(nextState, s);

        return nextState;
    }, initialApiState);

    useEffect(() => {
        dispatch('RESET');
    }, [enabled]);

    useEffect(() => {
        if (!enabled) {
            return;
        }

        let aborted = false;
        let loadingTimer;

        //I'ts better to show the loader instead of no-results
        if (state.items === initialApiState.items) {
            dispatch('REQ');
        } else {
            /**
             * Delaying the loading state when there are items
             * improves the update performance because dom is reused
             */
            loadingTimer = setTimeout(() => {
                dispatch('REQ');
            }, 1000);
        }

        let doRequest = () => {
            webApi(search, extraParams)
                .then(payload => {
                    if (aborted) {
                        return;
                    }

                    dispatch({
                        type: 'RES',
                        payload,
                    });
                })
                .catch(() => {
                    if (aborted) {
                        return;
                    }

                    dispatch('ERROR');
                })
                .then(() => {
                    clearTimeout(loadingTimer);
                });
        };

        let timer = setTimeout(doRequest, AUTOCOMPLETE_DEBOUNCE_TIME);

        //Abort
        return () => {
            aborted = true;
            clearTimeout(timer);
            clearTimeout(loadingTimer);
        };
    }, [search]);

    return state;
};

const initialState = {
    value: '',
    activeItem: null,
    focus: false,
    selected: null,
};

const autocompleteReducer = (state, action) => {
    switch (action.type) {
        case 'SET_VALUE': {
            return Object.assign({}, state, {
                value: action.payload,
            });
        }

        case 'SET_FOCUS': {
            return Object.assign({}, state, {
                focus: action.payload,
                value: focus ? state.value : '',
            });
        }

        case 'SET_SELECTED': {
            return Object.assign({}, state, {
                selected: action.payload,
                focus: false,
                value: '',
            });
        }

        default: {
            return state;
        }
    }
};

const useAutocomplete = ({
    getAutocompleteApi,
    defaultValue = '',
    minLength = 3,
    formatFn,
    onChange,
    extraParams,
}) => {
    const [state, dispatch] = useReducer(autocompleteReducer, {
        ...initialState,
        selected: defaultValue ? defaultValue : null,
    });

    const { value: search = '', focus, selected } = state;
    const open = focus && search.length >= minLength;

    const { items, loading } = useAutocompleteApi(
        search,
        extraParams,
        getAutocompleteApi,
        open
    );

    const navigation = useKeyboardNavigation(
        items.length,
        open && items.length > 0
    );

    const setFocus = value => {
        dispatch({ type: 'SET_FOCUS', payload: value });
    };

    const setValue = value => {
        dispatch({ type: 'SET_VALUE', payload: value });
    };

    const setSelectedIndex = index => {
        dispatch({ type: 'SET_SELECTED', payload: items[index] });
        onChange && onChange(items[index]);
        navigation.set(0);
    };

    const setSelected = item => {
        dispatch({ type: 'SET_SELECTED', payload: item });
        onChange && onChange(item);
        navigation.set(0);
    };

    const changeSelected = item => {
        dispatch({ type: 'SET_SELECTED', payload: item });
        navigation.set(0);
    };

    useListener(
        window,
        'keydown',
        evt => {
            let key = evt.which || evt.keyCode;
            switch (key) {
                case KEYS.tab:
                case KEYS.enter:
                    evt.preventDefault();
                    setSelected(navigation.value);
                    break;
            }
        },
        open
    );

    //The text displayed in the input
    const value = focus ? search : selected ? formatFn(selected) : '';

    return {
        highlight: navigation.value,
        handleItemHover: navigation.set,
        value,
        search, // used to highlight the searched string in the result items
        focus,
        items,
        selected,
        loading,
        open,
        setFocus,
        setValue,
        setSelected,
        changeSelected,
        setSelectedIndex,
    };
};

export default useAutocomplete;
